// deviceStore — the ONE seam that owns the `bilda.*` device-storage namespace.
//
// The device stratum (theme, guided-tour "seen", coaching flags, view state,
// keybindings, display name, panel layout, locale, last-visit/export prefs) is
// DEVICE UX state, not a domain fact (memory project_theme_outside_event_log):
// it round-trips through localStorage and is never emitted as an event. It is
// deliberately OUTSIDE the domain event-fold — but "outside the fold" is NOT
// "uncapturable": this module makes the whole namespace a captured, restorable
// TRACK so an Inspector snapshot can restore ALL strata (domain fold + device)
// and bring the app back byte-identical.
//
// ANTI-DRIFT (the point): this is the SINGLE place `window.localStorage` is
// touched. Every scattered accessor (theme.tsx, viewState.ts, tourSeen.ts, …)
// routes its raw reads/writes through `readDeviceKey`/`writeDeviceKey`/
// `removeDeviceKey` here, and `capture` reads the WHOLE `bilda.*` namespace by
// glob — so a newly-added key is captured automatically, nothing to forget.
// `deviceStore.no-raw-localStorage.test.ts` fails the moment any other file
// reaches for `localStorage.{get,set,remove}Item` directly, turning the
// discipline into a build gate rather than a promise.
//
// PORT + ADAPTERS (cross-platform by design): the capture/restore CONTRACT is
// an abstract `DeviceStorePort`; the backend is an adapter. `mkStorageDeviceStore`
// binds the contract to any `Storage` (the web/mobile-web `localStorage`
// adapter, and an in-memory fake in tests — the round-trip test runs the SAME
// contract against it). The pure serialize/parse/hash layer works on the keyed
// `DeviceBlob` and knows nothing about the backend, so a desktop-native adapter
// (Electron window bounds, keychain BYOK — see the report) is a later drop-in.
//
// PURE-CORE / REACT-IS-PURE-CORE: `serialize`/`parse`/`toExportableBlob` are
// pure and deterministic (a content hash of the blob is a pure function of these
// bytes — computed server-side over the exact serialization). Only the adapter
// (`captureFrom`/`restoreInto`, and the `window.localStorage` binding) is impure,
// and it is host-only — never called from the component tree.

export type { StorageLike };
export {
    isSecretKey,
    mkDeviceFoldStore,
    mkStorageDeviceStore,
    parseDeviceBlob,
    readDeviceKey,
    removeDeviceKey,
    serializeDeviceBlob,
    toExportableBlob,
    writeDeviceKey,
};

// The whole captured device namespace: a flat map of `bilda.*` key → raw stored
// string. Kept as opaque strings (each accessor owns its own parse) so this seam
// stays value-agnostic and captures ANY key by the glob below.
type DeviceBlob = { readonly entries: Readonly<Record<string, string>> };

// The capture/restore contract every platform adapter satisfies. `capture` reads
// the whole namespace into a blob; `restore` REPLACES the namespace with a blob
// (clears the existing `bilda.*` keys first, so a restore is exact, not a merge).
type DeviceStorePort = {
    readonly capture: () => DeviceBlob;
    readonly restore: (blob: DeviceBlob) => void;
};

// The subset of the DOM `Storage` API the adapter needs — so a plain in-memory
// fake (tests) satisfies the same maker as `window.localStorage` (browser).
type StorageLike = Pick<
    Storage,
    "getItem" | "setItem" | "removeItem" | "key" | "length"
>;

// The one namespace glob. Every device key is `bilda.<name>`; capture/restore
// operate on exactly this prefix so nothing outside the device stratum is ever
// touched (a foreign key on a shared origin, another app's storage).
const DEVICE_PREFIX = "bilda.";

const isDeviceKey = (key: string): boolean => key.startsWith(DEVICE_PREFIX);

// Secret-shaped keys never leave the machine in a `.vcr` export (security
// guardrail C). Matched by name so a future secret-bearing key is excluded from
// export automatically. The key is first normalized to space-separated words —
// camelCase boundaries (`authToken` → `auth token`) and `._-` delimiters both
// become spaces — so the credential shapes below match as WHOLE words. Word
// boundaries keep innocent lookalikes safe: `author`/`authorName` never match
// `token`, `theme` never matches anything.
const normalizeKeyWords = (key: string): string =>
    key
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[._-]+/g, " ")
        .toLowerCase();

const SECRET_WORD =
    /\b(token|secret|api ?key|credential|password|passwd|session|jwt)\b/;

const isSecretKey = (key: string): boolean =>
    SECRET_WORD.test(normalizeKeyWords(key));

const emptyDeviceBlob: DeviceBlob = { entries: {} };

// === Pure serialize / parse (platform-agnostic, deterministic) ===============

// Canonical (sorted-key) entry object — so the serialized bytes are stable
// regardless of insertion order, and a content hash over them is deterministic.
const canonicalEntries = (
    entries: Readonly<Record<string, string>>,
): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const key of Object.keys(entries).sort()) {
        const value = entries[key];
        if (value !== undefined) out[key] = value;
    }
    return out;
};

// The wire form: `{ schemaVersion, entries }` with sorted entries. The version
// tag is the migration seam (lifecycle design) — `parseDeviceBlob` degrades any
// unknown shape to empty rather than half-reading it.
const serializeDeviceBlob = (blob: DeviceBlob): string =>
    JSON.stringify({
        schemaVersion: 1,
        entries: canonicalEntries(blob.entries),
    });

const isRecord = (v: unknown): v is Record<string, unknown> =>
    typeof v === "object" && v !== null && !Array.isArray(v);

// Parse, don't validate: the blob text may come from disk (a snapshot) or a
// `.vcr` bundle written by ANY earlier build. Keep only device-prefixed keys
// with string values; anything else degrades away (never restored, never a
// crash). A malformed blob yields the empty namespace (a plain domain load).
const parseDeviceBlob = (raw: string): DeviceBlob => {
    let data: unknown;
    try {
        data = JSON.parse(raw);
    } catch {
        return emptyDeviceBlob;
    }
    const entriesRaw = isRecord(data) ? data.entries : undefined;
    if (!isRecord(entriesRaw)) return emptyDeviceBlob;
    const entries: Record<string, string> = {};
    for (const [key, value] of Object.entries(entriesRaw)) {
        if (isDeviceKey(key) && typeof value === "string") entries[key] = value;
    }
    return { entries };
};

// The export-safe projection: drop secret-shaped keys so a shareable `.vcr`
// bundle never carries credentials off the machine. The LOCAL snapshot keeps the
// full blob; only export narrows.
const toExportableBlob = (blob: DeviceBlob): DeviceBlob => {
    const entries: Record<string, string> = {};
    for (const [key, value] of Object.entries(blob.entries)) {
        if (!isSecretKey(key)) entries[key] = value;
    }
    return { entries };
};

// === Adapter — the impure backend binding (host-only) ========================

// Read the whole `bilda.*` namespace off a Storage into a blob.
const captureFrom = (storage: StorageLike): DeviceBlob => {
    const entries: Record<string, string> = {};
    for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (key !== null && isDeviceKey(key)) {
            const value = storage.getItem(key);
            if (value !== null) entries[key] = value;
        }
    }
    return { entries };
};

// REPLACE the `bilda.*` namespace with a blob: remove every existing device key
// first (so a stale key not in the blob is cleared — restore is exact), then
// write the blob's entries. Foreign keys are never touched.
const restoreInto = (storage: StorageLike, blob: DeviceBlob): void => {
    const existing: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (key !== null && isDeviceKey(key)) existing.push(key);
    }
    for (const key of existing) storage.removeItem(key);
    for (const [key, value] of Object.entries(blob.entries)) {
        storage.setItem(key, value);
    }
};

// Bind the capture/restore contract to any Storage. The web `localStorage`
// adapter and the in-memory test adapter are the SAME maker over different
// backends — the round-trip test exercises the contract through this.
const mkStorageDeviceStore = (storage: StorageLike): DeviceStorePort => ({
    capture: () => captureFrom(storage),
    restore: (blob) => restoreInto(storage, blob),
});

// The browser's `localStorage`, resolved lazily so importing this module in a
// non-browser context (unit tests under node) never throws at import time.
// `undefined` outside a browser → the per-key primitives no-op.
const browserStorage = (): StorageLike | undefined =>
    typeof window === "undefined" ? undefined : window.localStorage;

// === Per-key primitives — the ONLY window.localStorage in the app ============
// The scattered device-state accessors call THESE, not `window.localStorage`
// directly, so this file stays the single seam the ban-test enforces. Each
// asserts nothing about the value (parse lives in the owning accessor); they are
// thin, namespaced pass-throughs. `null` (absent) is preserved so every
// accessor's existing parse-don't-validate boundary is unchanged.

const readDeviceKey = (key: string): string | null =>
    browserStorage()?.getItem(key) ?? null;

const writeDeviceKey = (key: string, value: string): void => {
    browserStorage()?.setItem(key, value);
};

const removeDeviceKey = (key: string): void => {
    browserStorage()?.removeItem(key);
};

// === A device-kept value advanced by a fold (host-only) =====================
// One key's value, read lazily on first use, advanced only through its pure
// fold, written back on every change, and announced to its subscribers (a
// `useSyncExternalStore` source). The parse, serialize and fold are the
// owner's, and pure; this is only the impure loop around them.

type DeviceFoldStore<S, C> = {
    readonly get: () => S;
    readonly subscribe: (onChange: () => void) => () => void;
    readonly dispatch: (cmd: C) => void;
};

const mkDeviceFoldStore = <S, C>(
    key: string,
    parse: (raw: string | null) => S,
    serialize: (state: S) => string,
    fold: (state: S, cmd: C) => S,
): DeviceFoldStore<S, C> => {
    let state: S | undefined;
    const listeners = new Set<() => void>();
    const get = (): S => {
        if (state === undefined) state = parse(readDeviceKey(key));
        return state;
    };
    return {
        get,
        subscribe: (onChange) => {
            listeners.add(onChange);
            return () => listeners.delete(onChange);
        },
        dispatch: (cmd) => {
            const now = get();
            const next = fold(now, cmd);
            if (next === now) return;
            state = next;
            writeDeviceKey(key, serialize(next));
            for (const onChange of listeners) onChange();
        },
    };
};
